import Seo from "@/components/Seo";
import React from "react";
import {
  WhyHero, Heading, Reveal, PainGrid, FlowSequence, CompareTable,
  QuoteStrip, CheckList, LabelChips, WhyFinalCTA, PaymentPageMock, MilestoneTrackerMock,
} from "@/components/why/WhyParts";
import {
  BANK_PAINS, BANK_FLOW, BANK_COMPARE, BANK_QUOTES, BANK_WITH_WORKPAY, BANK_CUSTOMER_BENEFITS,
} from "@/data/content";

export default function WhyBankTransfers() {
  return (
    <main>
      <Seo path="/why-workpay/workpay-vs-bank-transfers" />
      <WhyHero
        eyebrow="Why Workpay"
        title={<>Bank transfers move money. <span className="text-gradient">Workpay moves projects forward.</span></>}
        subtitle="Bank transfers are useful, but they leave vendors chasing screenshots, approvals, and final payments. Workpay gives every project a clear payment workflow."
        secondaryLabel="See How It Works"
        visual={<PaymentPageMock />}
      />

      <section className="py-16 sm:py-24 bg-[#f7faf8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal><Heading overline="The gap" title="The problem with bank transfers" /></Reveal>
          <PainGrid items={BANK_PAINS} />
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Heading overline="What Workpay adds" title="Structure around the payment." subtitle="Workpay gives structure around the payment — not just a transfer." />
          </Reveal>
          <Reveal delay={0.08}><FlowSequence steps={BANK_FLOW} /></Reveal>
          <Reveal delay={0.12}>
            <div className="mt-12 max-w-sm mx-auto"><MilestoneTrackerMock /></div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#f7faf8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal><Heading overline="Side by side" title="Bank Transfer vs Workpay" /></Reveal>
          <Reveal delay={0.08}><CompareTable colA="Bank Transfer" colB="Workpay" rows={BANK_COMPARE} /></Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Heading
              overline="Sound familiar?"
              title={<>For vendors tired of asking, <span className="text-gradient">“Did you transfer?”</span></>}
              subtitle="Workpay helps vendors start projects with clarity instead of chasing payment proof and follow-up messages."
            />
          </Reveal>
          <QuoteStrip quotes={BANK_QUOTES} />
          <Reveal delay={0.1}>
            <div className="mt-10 max-w-xl mx-auto"><CheckList title="With Workpay" items={BANK_WITH_WORKPAY} /></div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#f7faf8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Heading
              overline="For customers"
              title="Customers get clarity too."
              subtitle="Workpay helps customers see project terms, track progress, approve milestones, and raise issues clearly before payment becomes a fight."
            />
          </Reveal>
          <LabelChips items={BANK_CUSTOMER_BENEFITS.map((b) => ({ icon: "ShieldCheck", label: b }))} />
        </div>
      </section>

      <WhyFinalCTA
        title={<>Stop running projects through <span className="text-[#21c55e]">bank screenshots.</span></>}
        subtitle="Use Workpay to collect project payments, track milestones, and get paid with more clarity."
      />
    </main>
  );
}
